import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  ChevronDown, ChevronUp, ExternalLink, X, RefreshCw,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Bell, BellOff, Search, Tag, Activity, Newspaper, TrendingUp,
} from 'lucide-react';
import { getCachedPortfolioMap, getCachedWatchlistMap } from '../utils/portfolioWatchlistCache';
import { authFetch } from '../utils/authFetch';

const ITEMS_PER_PAGE = 10;

/** Strip venue suffix from compound symbol_id (e.g. AAPL_NYSE → AAPL). */
function stripExchangeSuffix(value) {
  if (!value || typeof value !== 'string') return '';
  const idx = value.lastIndexOf('_');
  return idx > 0 ? value.slice(0, idx) : value;
}

/** Display ticker without exchange suffix — matches news alert symbol formatting. */
function getDisplaySymbol(notification) {
  const rawSymbol = notification?.symbol;
  if (rawSymbol && typeof rawSymbol === 'string' && !rawSymbol.includes('_')) {
    return rawSymbol;
  }
  const symbolId = notification?.symbol_id || rawSymbol;
  return stripExchangeSuffix(symbolId) || rawSymbol || 'N/A';
}

function getStockSymbolId(notification) {
  return notification.symbol_id || notification.symbol || '';
}

function formatNotificationDate(insertDate, pubDatetime) {
  if (pubDatetime) {
    return new Date(pubDatetime).toLocaleString('en-CA', {
      timeZone: 'America/New_York',
      hour12: false,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  if (!insertDate) return '—';
  const parsed = new Date(insertDate);
  if (Number.isNaN(parsed.getTime())) return insertDate;
  return parsed.toLocaleString('en-CA', {
    timeZone: 'America/New_York',
    hour12: false,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getAlertTypeLabel(type) {
  return type === 'stock_alert' ? 'Stock Alert' : 'News Alert';
}

function getSignalClass(status) {
  const s = (status || '').toLowerCase();
  if (s === 'buy') return 'msg-badge--buy';
  if (s === 'sell') return 'msg-badge--sell';
  if (s === 'hold') return 'msg-badge--hold';
  return 'msg-badge--info';
}

function NotificationCard({ notification, expanded, onToggle, onDelete }) {
  const symbolLabel = getDisplaySymbol(notification);
  const symbolId = getStockSymbolId(notification);
  const stockPath = `/stock/${encodeURIComponent(String(symbolId).toUpperCase())}`;

  return (
    <article className="msg-card" aria-labelledby={`msg-${notification.note_id}-title`}>
      <div className="msg-card-header">
        <div className="msg-card-header-left">
          <a
            href={stockPath}
            target="_blank"
            rel="noopener noreferrer"
            className="msg-symbol-chip"
            title="View stock details"
          >
            {symbolLabel}
          </a>
          <span className={`msg-badge msg-badge--type ${notification.type === 'stock_alert' ? 'msg-badge--stock' : 'msg-badge--news'}`}>
            {notification.type === 'stock_alert' ? <TrendingUp size={11} aria-hidden /> : <Newspaper size={11} aria-hidden />}
            {getAlertTypeLabel(notification.type)}
          </span>
          {notification.status && (
            <span className={`msg-badge ${getSignalClass(notification.status)}`}>
              {String(notification.status).toUpperCase()}
            </span>
          )}
        </div>
        <time className="msg-card-date" dateTime={notification.pub_datetime || notification.insert_date}>
          {formatNotificationDate(notification.insert_date, notification.pub_datetime)}
        </time>
      </div>

      <p id={`msg-${notification.note_id}-title`} className="msg-card-message">
        {notification.message}
      </p>

      {notification.sentiment_data && (
        <div className="msg-sentiment-row">
          {(notification.sentiment_data.rating || notification.sentiment_data.rating_tag) && (
            <div className="msg-sentiment-rating">
              {notification.sentiment_data.rating && (
                <span className="msg-rating-value">
                  Rating {notification.sentiment_data.rating}
                  <span className="msg-rating-denom">/10</span>
                </span>
              )}
              {notification.sentiment_data.rating_tag && (
                <span className="msg-rating-tag">{notification.sentiment_data.rating_tag}</span>
              )}
            </div>
          )}
          <Activity size={15} className="msg-sentiment-icon" aria-label="AI sentiment analysis" />
        </div>
      )}

      {notification.key_list && notification.key_list.length > 0 && (
        <div className="msg-tags">
          <span className="msg-tags-label">Matched keywords</span>
          <div className="msg-tags-list">
            {notification.key_list.map((keyword, index) => (
              <span key={index} className="msg-tag">
                <Tag size={11} aria-hidden />
                {keyword}
              </span>
            ))}
          </div>
        </div>
      )}

      {expanded && (
        <div className="msg-card-details">
          {notification.type === 'stock_alert' ? (
            <p className="msg-meta-line">Stock ID: {notification.stock_id || 'N/A'}</p>
          ) : (
            <p className="msg-meta-line">News ID: {notification.news_id || 'N/A'}</p>
          )}
          <p className="msg-meta-line">Alert ID: {notification.note_id}</p>

          {notification.stats?.key_metrics?.length > 0 && (
            <div className="msg-detail-block">
              <h4 className="msg-detail-title">Key Metrics</h4>
              <ul className="msg-detail-list">
                {notification.stats.key_metrics.map((metric, index) => (
                  <li key={index}>
                    <strong>{metric.bullet_point}:</strong> {metric.details}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {notification.sentiment_data && (
            <div className="msg-detail-block msg-sentiment-detail">
              <h4 className="msg-detail-title">Sentiment Analysis</h4>

              {notification.sentiment_data.rating && (
                <div className="msg-sentiment-item">
                  <span className="msg-sentiment-label">Rating</span>
                  <span className="msg-sentiment-value">
                    {notification.sentiment_data.rating}
                    <span className="msg-rating-denom">/10</span>
                    {notification.sentiment_data.rating_tag && (
                      <span className="msg-rating-tag"> ({notification.sentiment_data.rating_tag})</span>
                    )}
                  </span>
                </div>
              )}

              {notification.sentiment_data.company_info && (
                <div className="msg-sentiment-item">
                  <span className="msg-sentiment-label">Company</span>
                  <span className="msg-sentiment-value">{notification.sentiment_data.company_info}</span>
                </div>
              )}

              {notification.sentiment_data.valuation && (
                <div className="msg-sentiment-item">
                  <span className="msg-sentiment-label">Valuation</span>
                  <span className="msg-sentiment-value">{notification.sentiment_data.valuation}</span>
                </div>
              )}

              {[
                { key: 'key_drivers', label: 'Key Drivers' },
                { key: 'positive_factors', label: 'Positive Factors', className: 'positive' },
                { key: 'negative_factors', label: 'Negative Factors', className: 'negative' },
                { key: 'neutral_factors', label: 'Neutral Factors', className: 'neutral' },
              ].map(({ key, label, className }) =>
                notification.sentiment_data[key]?.length > 0 ? (
                  <div key={key} className={`msg-sentiment-section ${className || ''}`}>
                    <span className="msg-sentiment-label">{label}</span>
                    <ul className="msg-detail-list">
                      {notification.sentiment_data[key].map((item, index) => (
                        <li key={index}>
                          <strong>{item.bullet_point}:</strong> {item.details}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null
              )}
            </div>
          )}
        </div>
      )}

      <div className="msg-card-actions">
        <button
          type="button"
          onClick={() => onToggle(notification.note_id)}
          className="msg-action-btn msg-action-btn--ghost"
          aria-expanded={expanded}
        >
          {expanded ? <>Less <ChevronUp size={14} /></> : <>More <ChevronDown size={14} /></>}
        </button>

        {notification.news_url ? (
          <a
            href={notification.news_url}
            target="_blank"
            rel="noopener noreferrer"
            className="msg-action-btn msg-action-btn--primary"
          >
            View <ExternalLink size={14} />
          </a>
        ) : (
          <a
            href={stockPath}
            target="_blank"
            rel="noopener noreferrer"
            className="msg-action-btn msg-action-btn--primary"
          >
            View Stock <ExternalLink size={14} />
          </a>
        )}

        <button
          type="button"
          onClick={() => onDelete(notification.note_id)}
          className="msg-action-btn msg-action-btn--danger"
          aria-label="Delete notification"
        >
          <X size={14} />
          <span className="msg-action-label">Delete</span>
        </button>
      </div>
    </article>
  );
}

function Messages() {
  const [notifications, setNotifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedNotifications, setExpandedNotifications] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [bannerMessage, setBannerMessage] = useState(null);
  const [liveUpdates, setLiveUpdates] = useState(false);

  const checkIntervalRef = useRef(null);
  const hourlyTimeoutRef = useRef(null);

  const [user] = useState(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const userEmail = user?.email;

  const showMessage = (type, text) => {
    setBannerMessage({ type, text });
    setTimeout(() => setBannerMessage(null), 3000);
  };

  const transformStockAlertToNotification = useCallback((symbolKey, alert) => {
    const updateAt = alert.update_at || '';
    let insertDate = '';
    if (updateAt && updateAt.length >= 14) {
      const year = updateAt.substring(0, 4);
      const month = updateAt.substring(4, 6);
      const day = updateAt.substring(6, 8);
      const hour = updateAt.substring(8, 10);
      const minute = updateAt.substring(10, 12);
      const second = updateAt.substring(12, 14);
      insertDate = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
    } else {
      insertDate = new Date().toLocaleString('en-CA', { timeZone: 'America/New_York', hour12: false }).replace(', ', 'T');
    }

    const symbolId = alert.symbol_id || symbolKey;
    const displaySymbol = alert.symbol || stripExchangeSuffix(symbolId);

    return {
      note_id: alert.alert_id || `${symbolKey}_${alert.stock_id}`,
      stock_id: alert.stock_id,
      type: alert.type || 'stock_alert',
      level: alert.level || 2,
      status: alert.status || 'hold',
      news_url: null,
      insert_date: insertDate,
      pub_datetime: null,
      symbol_id: symbolId,
      symbol: displaySymbol,
      message: alert.message || '',
      key_list: alert.key_list || [],
      stats: alert.stats || null,
      sentiment_data: null,
    };
  }, []);

  const getCachedSymbols = useCallback(() => {
    if (!userEmail) {
      return { portfolioSymbols: [], watchlistSymbols: [] };
    }

    const portfolioSymbols = new Set();
    const watchlistSymbols = new Set();

    try {
      const cachedPortfolioMap = getCachedPortfolioMap(userEmail);
      if (cachedPortfolioMap) {
        Object.values(cachedPortfolioMap).forEach((portfolio) => {
          if (portfolio && typeof portfolio === 'object') {
            Object.keys(portfolio).forEach((symbol) => {
              if (symbol && typeof symbol === 'string') {
                portfolioSymbols.add(symbol.toUpperCase());
              }
            });
          }
        });
      }

      const cachedWatchlistMap = getCachedWatchlistMap(userEmail);
      if (cachedWatchlistMap) {
        Object.values(cachedWatchlistMap).forEach((watchlist) => {
          if (Array.isArray(watchlist)) {
            watchlist.forEach((symbol) => {
              if (symbol && typeof symbol === 'string') {
                watchlistSymbols.add(symbol.toUpperCase());
              }
            });
          }
        });
      }
    } catch (err) {
      console.error('Error extracting cached symbols:', err);
    }

    return {
      portfolioSymbols: Array.from(portfolioSymbols),
      watchlistSymbols: Array.from(watchlistSymbols),
    };
  }, [userEmail]);

  const fetchStockAlerts = useCallback(async () => {
    if (!userEmail) return [];

    const fetchWithRetry = async (retryCount = 0) => {
      try {
        const { portfolioSymbols, watchlistSymbols } = getCachedSymbols();

        const response = await authFetch(`/api/stock_alerts/${userEmail}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ portfolioSymbols, watchlistSymbols }),
        });

        if (!response.ok) {
          throw new Error('Failed to fetch stock alerts');
        }

        const data = await response.json();

        if (data.processing && retryCount < 3) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
          await new Promise((resolve) => setTimeout(resolve, delay));
          return fetchWithRetry(retryCount + 1);
        }

        const stockAlerts = data.stockAlerts || {};
        return Object.keys(stockAlerts).map((symbol) =>
          transformStockAlertToNotification(symbol, stockAlerts[symbol])
        );
      } catch (err) {
        console.error('Error fetching stock alerts:', err);
        return [];
      }
    };

    return fetchWithRetry();
  }, [userEmail, transformStockAlertToNotification, getCachedSymbols]);

  const fetchNotifications = useCallback(async () => {
    try {
      const notificationsResponse = await authFetch(`/api/notifications/${userEmail}`);
      if (!notificationsResponse.ok) {
        throw new Error('Failed to fetch notifications');
      }
      const notificationsData = await notificationsResponse.json();
      const newsNotifications = notificationsData.notifications || [];
      const stockAlertNotifications = await fetchStockAlerts();
      const allNotifications = [...newsNotifications, ...stockAlertNotifications];

      allNotifications.sort((a, b) => {
        if (a.pub_datetime && b.pub_datetime) {
          return new Date(b.pub_datetime) - new Date(a.pub_datetime);
        }
        if (a.pub_datetime) return -1;
        if (b.pub_datetime) return 1;
        const dateA = a.insert_date ? new Date(a.insert_date) : new Date(0);
        const dateB = b.insert_date ? new Date(b.insert_date) : new Date(0);
        return dateB - dateA;
      });

      setNotifications(allNotifications);
      setTotalPages(Math.ceil(allNotifications.length / ITEMS_PER_PAGE));
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.message);
    }
  }, [userEmail, fetchStockAlerts]);

  const checkForNotifications = async (showAlerts = true, fullRefresh = false) => {
    try {
      const currentDate = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

      if (showAlerts) {
        setRefreshing(true);
      }

      if (fullRefresh) {
        setNotifications([]);
      }

      const { portfolioSymbols, watchlistSymbols } = getCachedSymbols();

      const response = await authFetch(`/api/notifications/${userEmail}/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: currentDate,
          fullRefresh,
          portfolioSymbols,
          watchlistSymbols,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to check for notifications');
      }

      const data = await response.json();

      if (data.cleared && !fullRefresh) {
        setNotifications([]);
      }

      const hasNewNotifications = data.newNotifications && data.newNotifications.length > 0;

      if (hasNewNotifications) {
        window.dispatchEvent(new Event('new-notification'));
      }

      if (hasNewNotifications || fullRefresh || data.cleared) {
        await fetchNotifications();
      }

      if (showAlerts) {
        if (fullRefresh) {
          showMessage(
            'success',
            hasNewNotifications
              ? `Notifications refreshed. Found ${data.newNotifications.length} alerts!`
              : 'Notifications cleared. No alerts found for your keywords.'
          );
        } else if (data.cleared) {
          showMessage('success', 'Date has changed. Notifications have been cleared.');
        } else if (hasNewNotifications) {
          showMessage('success', `Found ${data.newNotifications.length} new notifications!`);
        } else {
          showMessage('success', 'No new notifications found for your keywords.');
        }
      }
    } catch (err) {
      console.error('Error checking for notifications:', err);
      if (showAlerts) {
        showMessage('error', err.message || 'Failed to check for notifications.');
      }
    } finally {
      if (showAlerts) {
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        if (!userEmail) {
          setError('Please sign in to configure and receive alerts.');
        } else {
          setError(null);
        }
        await checkForNotifications(false, true);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [userEmail]);

  useEffect(() => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
    if (hourlyTimeoutRef.current) {
      clearTimeout(hourlyTimeoutRef.current);
      hourlyTimeoutRef.current = null;
    }

    checkIntervalRef.current = setInterval(
      () => checkForNotifications(false),
      liveUpdates ? 30000 : 3600000
    );

    return () => {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      if (hourlyTimeoutRef.current) clearTimeout(hourlyTimeoutRef.current);
    };
  }, [liveUpdates, userEmail]);

  const filteredNotifications = useMemo(() => {
    if (!searchQuery.trim()) return notifications;
    const q = searchQuery.trim().toLowerCase();
    return notifications.filter((n) => {
      const message = (n.message || '').toLowerCase();
      const symbol = getDisplaySymbol(n).toLowerCase();
      const symbolId = ((n.symbol_id || n.symbol) || '').toLowerCase();
      const keys = (n.key_list || []).join(' ').toLowerCase();
      return message.includes(q) || symbol.includes(q) || symbolId.includes(q) || keys.includes(q);
    });
  }, [notifications, searchQuery]);

  const alertSummary = useMemo(() => {
    const newsCount = filteredNotifications.filter((n) => n.type !== 'stock_alert').length;
    const stockCount = filteredNotifications.filter((n) => n.type === 'stock_alert').length;
    return { total: filteredNotifications.length, newsCount, stockCount };
  }, [filteredNotifications]);

  useEffect(() => {
    const newTotalPages = Math.ceil(filteredNotifications.length / ITEMS_PER_PAGE);
    setTotalPages(newTotalPages);
    setCurrentPage(1);
  }, [filteredNotifications.length]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handleDeleteNotification = async (noteId) => {
    try {
      const response = await authFetch(
        `/api/notifications/${encodeURIComponent(userEmail)}/${encodeURIComponent(noteId)}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete notification');
      }
      setNotifications((prev) => prev.filter((note) => note.note_id !== noteId));
      setError(null);
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError(err.message);
    }
  };

  const toggleNotification = (id) => {
    setExpandedNotifications((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleFirstPage = () => setCurrentPage(1);
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const handleLastPage = () => setCurrentPage(totalPages);

  const currentNotifications = filteredNotifications.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const useTwoColumns = filteredNotifications.length > 1;

  if (loading) {
    return (
      <div className="page-content messages-page pw-page">
        <div className="pw-header">
          <div className="pw-header-left">
            <div className="pw-header-text">
              <h2 className="pw-title">
                <Bell size={24} className="pw-title-icon pw-title-icon--messages" />
                <span>My Notifications</span>
              </h2>
              <p className="pw-subtitle">News &amp; stock alerts for your watchlist</p>
            </div>
          </div>
        </div>
        <div className="pw-loading">
          <div className="pw-spinner" />
          <p>Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content messages-page pw-page">
      <div className="pw-header">
        <div className="pw-header-left">
          <div className="pw-header-text">
            <h2 className="pw-title">
              <Bell size={24} className="pw-title-icon pw-title-icon--messages" />
              <span>My Notifications</span>
            </h2>
            <p className="pw-subtitle">News &amp; stock alerts for your watchlist</p>
          </div>
        </div>
        <div className="pw-header-actions msg-header-actions">
          <div
            className={`live-updates-toggle ${liveUpdates ? 'active' : ''}`}
            role="group"
            aria-label="Alert refresh frequency"
            title={liveUpdates ? 'Live updates enabled (checking every 30 seconds)' : 'Live updates disabled (checking hourly)'}
          >
            <button
              type="button"
              className="toggle-option hourly"
              onClick={() => setLiveUpdates(false)}
              aria-pressed={!liveUpdates}
            >
              <BellOff size={14} className="toggle-icon" aria-hidden />
              <span>Hourly</span>
            </button>
            <button
              type="button"
              className="toggle-option live"
              onClick={() => setLiveUpdates(true)}
              aria-pressed={liveUpdates}
            >
              <Bell size={14} className="toggle-icon" aria-hidden />
              <span>Live</span>
            </button>
          </div>
          <button
            type="button"
            onClick={() => checkForNotifications(true, true)}
            className="pw-icon-btn"
            disabled={refreshing || !userEmail}
            title="Check all news for new alerts"
            aria-label="Check for new alerts"
          >
            <RefreshCw size={22} className={refreshing ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      {bannerMessage && (
        <div className={`profile-message profile-message-${bannerMessage.type} msg-banner`} role="status">
          {bannerMessage.text}
        </div>
      )}

      {error && (
        <div className="msg-notice" role="alert">
          <p>{error}</p>
          {!userEmail && (
            <p>
              <a href="/login" target="_blank" rel="noopener noreferrer" className="msg-notice-link">Sign in</a> to set up keyword alerts.
            </p>
          )}
        </div>
      )}

      {notifications.length > 0 && (
        <div className="msg-summary-card">
          <div className="msg-summary-main">
            <span className="msg-summary-label">
              <Bell size={14} aria-hidden />
              Active Alerts
            </span>
            <span className="msg-summary-value">{alertSummary.total}</span>
            <span className="msg-summary-suffix">
              {searchQuery.trim()
                ? (alertSummary.total === 1 ? 'matching alert' : 'matching alerts')
                : (liveUpdates ? 'Refreshing every 30s' : 'Refreshing hourly')}
            </span>
          </div>
          <div className="msg-summary-stats">
            <div className="msg-summary-stat">
              <span className="msg-summary-stat-label">News</span>
              <span className="msg-summary-stat-value">{alertSummary.newsCount}</span>
            </div>
            <div className="msg-summary-stat">
              <span className="msg-summary-stat-label">Stock</span>
              <span className="msg-summary-stat-value">{alertSummary.stockCount}</span>
            </div>
          </div>
        </div>
      )}

      <div className="pw-data-card msg-data-card">
        <div className="pw-data-header">
          <div className="pw-data-header-left">
            <h3>Recent Alerts</h3>
            <span className="pw-data-count">{filteredNotifications.length} total</span>
          </div>
          <div className="pw-data-header-right">
            <div className="pw-search-wrap">
              <Search size={14} className="pw-search-icon" aria-hidden />
              <input
                type="search"
                className="pw-search-input"
                placeholder="Search messages, symbols, keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search notifications"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="pw-search-clear"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        {filteredNotifications.length === 0 ? (
          <div className="pw-empty-state msg-empty-state">
            <Bell size={40} aria-hidden />
            <h3>{notifications.length === 0 ? 'No Alerts Yet' : 'No Matching Alerts'}</h3>
            <p>
              {notifications.length === 0
                ? 'Manage keywords in Account settings to get notified when they appear in news.'
                : 'Try adjusting your search terms to find what you need.'}
            </p>
            {!userEmail && (
              <a href="/login" target="_blank" rel="noopener noreferrer" className="pw-btn pw-btn-primary">Sign in</a>
            )}
          </div>
        ) : (
          <>
            <div
              className={`msg-notifications-grid${useTwoColumns ? ' msg-notifications-grid--two-col' : ''}`}
            >
              {currentNotifications.map((notification) => (
                <NotificationCard
                  key={notification.note_id}
                  notification={notification}
                  expanded={!!expandedNotifications[notification.note_id]}
                  onToggle={toggleNotification}
                  onDelete={handleDeleteNotification}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="pw-pagination">
                <button type="button" onClick={handleFirstPage} disabled={currentPage === 1} title="First Page" aria-label="First page">
                  <ChevronsLeft size={18} />
                </button>
                <button type="button" onClick={handlePrevPage} disabled={currentPage === 1} title="Previous Page" aria-label="Previous page">
                  <ChevronLeft size={18} />
                </button>
                <span className="pw-page-info">Page {currentPage} of {totalPages}</span>
                <button type="button" onClick={handleNextPage} disabled={currentPage === totalPages} title="Next Page" aria-label="Next page">
                  <ChevronRight size={18} />
                </button>
                <button type="button" onClick={handleLastPage} disabled={currentPage === totalPages} title="Last Page" aria-label="Last page">
                  <ChevronsRight size={18} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Messages;
